import { action, observable, flow, makeObservable } from 'mobx';
import { ApiResponse, GlobalStore } from '@roc/feature-app-core';
import { CounterPartyExpensesTab, HistoryEntry } from '../types/types';
import { SpecialServicingBaseStore } from './specialServicingBaseStore';
import { Option } from '@roc/ui/utils';
import { ProtectiveAdvanceFormStore } from './protectiveAdvanceFormStore';

export class PayablesDashboardStore extends SpecialServicingBaseStore {
  vendorOptions: Option[] = [];
  protectiveAdvanceFormStore: ProtectiveAdvanceFormStore;
  historyModalOpen: boolean = false;
  historyAdjustmentOnly: boolean = false;
  historyRecord: any = null;
  payableHistory: HistoryEntry[] = [];
  historyLoading: boolean = false;

  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.tab = CounterPartyExpensesTab.PENDING_PAYABLE;
    this.protectiveAdvanceFormStore = new ProtectiveAdvanceFormStore(globalStore, this.service);

    makeObservable(this, {
      fetchEntityNames: flow,
      performAction: flow,
      performActions: flow,
      vendorOptions: observable,
      protectiveAdvanceFormStore: observable,
      createCreditMemoFromPayables: flow,
      createCreditMemoFromPayable: flow,
      updatePayableAdjustment: flow,
      historyModalOpen: observable,
      historyRecord: observable,
      payableHistory: observable,
      historyLoading: observable,
      historyAdjustmentOnly: observable,
      openHistoryModal: action,
      closeHistoryModal: action,
      fetchPayableHistory: flow,
      openHistoryAdjustmentModal: action,
    });
  };

  openHistoryModal(record: any) {
    this.historyRecord = record;
    this.historyModalOpen = true;
    this.fetchPayableHistory(record.recordId);
  }

  openHistoryAdjustmentModal(record: any) {
    this.historyAdjustmentOnly = true;
    this.openHistoryModal(record);
  }

  closeHistoryModal() {
    this.historyAdjustmentOnly = false;
    this.historyModalOpen = false;
    this.historyRecord = null;
    this.payableHistory = [];
  }

  *fetchPayableHistory(payableId: number | string) {
    this.historyLoading = true;
    try {
      const response: ApiResponse = yield this.service.getPayableHistory(payableId);
      this.payableHistory = response.data.data;
      this.historyLoading = false;
    } catch (error) {
      this.historyLoading = false;
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching payable history.',
      });
    }
  }

  *fetchEntityNames() {
    try {
      const response: ApiResponse = yield this.service.getVendorsNames();
      this.entityList = response.data.data.map(value => (
        { label: value.vendorName, value: value.companyId }
      ));
      this.vendorOptions = this.entityList.map(value => (
        { label: value.label, value: value.label }
      ));
    }
    catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching vendor names.',
      });
    }
  };


  reset() {
    this.tab = CounterPartyExpensesTab.PENDING_PAYABLE;
    this.counterPartyExpenseGridStore.reset();
  };

  *performActions(status: string) {
    const response = yield this.service.performPayableAction(this.recordsIds, status);
  }

  *performAction(id: number, status: string) {
    const idList: number[] = [id];
    try {
      const response = yield this.service.performPayableAction(idList, status);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Status updated successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating status.',
      });
    }
  }

  *createCreditMemoFromPayables() {
    try {
      const response = yield this.service.createCreditMemoFromPayable(this.recordsIds);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Credit memo created successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while creating credit memo.',
      });
    }
  }

  *createCreditMemoFromPayable(id: number, amount: number, comment: string) {
    try {
      const response = yield this.service.createCreditMemoFromPayables([id], amount, comment);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Credit memo created successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while creating credit memo.',
      });
    }
  }

  *updatePayableAdjustment(payableId: number, adjustment: number, comment: string) {
    try {
      const response = yield this.service.updatePayableAdjustment(payableId, adjustment, comment);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Adjustment updated successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating adjustment.',
      });
    }
  }
}
